import { useWorkspace } from '@/features/workspaces/hooks';
import { StepPageActions } from '@/global/components';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { BaseAudience } from '@/models/audiences';
import { ConnectorFlowDTO, CustomConnectorDto } from '@/models/connectors';
import { useCreateFlowInstanceMutation, useGetConnectorInstancesQuery } from '@/services/endpoints/workspace-connectors';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

type CustomConnectorSyncStepProps = {
  audience: BaseAudience;
  connector: CustomConnectorDto;
  flow: ConnectorFlowDTO;
  params: Record<string, any>;
  mappings: Record<string, any>;
  onSyncSuccessful: () => void;
  onCancel: () => void;
};

export const CustomConnectorSyncStep: FC<CustomConnectorSyncStepProps> = ({
  audience,
  connector,
  flow,
  params,
  mappings,
  onSyncSuccessful,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { id: workspaceId } = useWorkspace();
  const [createFlowInstance, { isLoading: isCreatingFlow }] = useCreateFlowInstanceMutation();
  const { data: instances } = useGetConnectorInstancesQuery({ workspaceId });

  const handleSync = async () => {
    try {
      const instance = instances?.find((inst) => inst.connectorId === connector.id);

      if (!instance) {
        console.error('No connector instance found for this connector');
        return;
      }

      const paramsArray = Object.entries(params).map(([key, value]) => ({
        key,
        value,
      }));

      const mappingsArray = Object.entries(mappings).map(([source, target]) => ({
        source,
        target: target as string,
      }));

      await createFlowInstance({
        workspaceId,
        id: flow.id,
        connectorInstanceId: instance.id,
        audienceId: audience.id,
        params: paramsArray,
        mappings: mappingsArray,
      }).unwrap();

      onSyncSuccessful();
    } catch (error) {
      console.error('Failed to create flow instance:', error);
    }
  };

  const cancelButton: StepPageActionsButton = {
    text: 'COMMON:cancel',
    onClick: onCancel,
  };

  const syncButton: StepPageActionsButton = {
    text: 'COMMON:confirm',
    isLoading: isCreatingFlow,
    onClick: handleSync,
  };

  return (
    <div className='flex grow flex-col gap-6 p-6'>
      <div className="flex flex-col border-b border-secondary-border pb-8">
        <h2 className="text-lg font-semibold leading-7 text-primary-900">{t('ALL:destinations.steps.reviewAndActivate.title')}</h2>
        <h4 className="text-sm leading-5 text-tertiary-600">{t('ALL:destinations.steps.reviewAndActivate.description')}</h4>
      </div>

      <div className='space-y-6'>
        <div className='rounded-lg bg-gray-50 p-4'>
          <h3 className='mb-4 text-lg font-medium'>{t('ALL:destinations.steps.reviewAndActivate.configurationSummary')}</h3>
          
          <div className='space-y-4'>
            <div>
              <h4 className='font-medium text-gray-900'>{t('ALL:destinations.steps.reviewAndActivate.audience')}</h4>
              <p className='text-sm text-gray-600'>{audience.name}</p>
            </div>
            
            <div>
              <h4 className='font-medium text-gray-900'>{t('ALL:destinations.steps.reviewAndActivate.connector')}</h4>
              <p className='text-sm text-gray-600'>{connector.name}</p>
            </div>
            
            <div>
              <h4 className='font-medium text-gray-900'>{t('ALL:destinations.steps.reviewAndActivate.flow')}</h4>
              <p className='text-sm text-gray-600'>{flow.name}</p>
            </div>
            
            {Object.keys(params).length > 0 && (
              <div>
                <h4 className='font-medium text-gray-900'>{t('ALL:destinations.steps.reviewAndActivate.parameters')}</h4>
                <div className='text-sm text-gray-600'>
                  {Object.entries(params).map(([key, value]) => (
                    <div key={key} className='flex justify-between'>
                      <span>{key}:</span>
                      <span>{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {Object.keys(mappings).length > 0 && (
              <div>
                <h4 className='font-medium text-gray-900'>{t('ALL:destinations.steps.reviewAndActivate.fieldMappings')}</h4>
                <div className='text-sm text-gray-600'>
                  {Object.entries(mappings).map(([key, value]) => (
                    <div key={key} className='flex justify-between'>
                      <span>{key}:</span>
                      <span>{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className='rounded-lg bg-blue-50 p-4'>
          <h4 className='mb-2 font-medium text-blue-900'>{t('ALL:destinations.steps.reviewAndActivate.whatHappensNext')}</h4>
          <ul className='space-y-1 text-sm text-blue-800'>
            <li>• {t('ALL:destinations.steps.reviewAndActivate.syncDescription', { connectorName: connector.name })}</li>
            <li>• {t('ALL:destinations.steps.reviewAndActivate.automaticSync')}</li>
            <li>• {t('ALL:destinations.steps.reviewAndActivate.monitorSync')}</li>
          </ul>
        </div>
      </div>

      <StepPageActions primaryButton={syncButton} secondaryButton={cancelButton} />
    </div>
  );
};
